import { infiniteQueryOptions, queryOptions, keepPreviousData } from "@tanstack/react-query";
import {
  fetchIamUsersPage,
  fetchUser,
  fetchUserAccess,
  fetchUsersAccess,
  fetchIamTenantsPage,
  fetchTenant,
  fetchAccessByTenant,
  fetchAccessGroupsByTenants,
  fetchAccessGroup,
  fetchIamStats,
  fetchAllIndustries,
  fetchIndustryBrandCounts,
  fetchIndustryBrandOptions,
  fetchAllMarketCatalogue,
  fetchAllInvites,
  fetchIamAccessPage,
  fetchAdminTenantSettings,
  fetchAdminTenantSyncRuns,
  fetchAdminTenantPreflightMarketRefs,
  fetchAccessTemplates,
  fetchAccessTemplate,
  type IamWindow,
  type IamAccessListFilters,
  type PaginatedTenants,
  type IamUsersListFilters,
  type IamTenantsListFilters,
  type InviteListFilters,
  type AccessTemplateListFilters,
} from "@/features/identity-hub/api/identity-hub.api";
import { INDUSTRY_BRANDS_STALE_TIME_MS } from "@/lib/query-constants";

// ─── Key Factory ────────────────────────────────────────────────────

export const identityHubKeys = {
  all: ["identity-hub"] as const,
  users: () => ["identity-hub", "users"] as const,
  user: (userId: string) => ["identity-hub", "users", userId] as const,
  usersList: (filters: IamUsersListFilters) => ["identity-hub", "users", "list", filters] as const,
  userAccess: (userId: string) =>
    ["identity-hub", "user-access", userId] as const,
  /** Batched per-user access read for the People tab. Ids sorted so key is order-independent. */
  usersAccess: (userIds: string[]) =>
    ["identity-hub", "users-access", [...userIds].sort()] as const,
  usersAccessPrefix: () => ["identity-hub", "users-access"] as const,
  userAccessPrefix: () => ["identity-hub", "user-access"] as const,
  tenants: (opts: { includeArchived?: boolean } = {}) =>
    ["identity-hub", "tenants", { includeArchived: !!opts.includeArchived }] as const,
  /** Broad prefix matching every tenants query (list, infinite, picker, single). */
  tenantsPrefix: () => ["identity-hub", "tenants"] as const,
  tenantsList: (filters: IamTenantsListFilters) =>
    ["identity-hub", "tenants", "list", filters] as const,
  tenant: (tenantId: string) => ["identity-hub", "tenants", tenantId] as const,
  tenantPicker: () => ["identity-hub", "tenants", "picker"] as const,
  accessByTenant: (tenantId: string, opts: { includeArchived?: boolean } = {}) =>
    ["identity-hub", "access-by-tenant", tenantId, { includeArchived: !!opts.includeArchived }] as const,
  /** Batched per-tenant access-group read for the IAM dashboard. Ids sorted so key is order-independent. */
  accessByTenants: (tenantIds: string[], opts: { includeArchived?: boolean } = {}) =>
    ["identity-hub", "access-by-tenants", [...tenantIds].sort(), { includeArchived: !!opts.includeArchived }] as const,
  accessByTenantsPrefix: () => ["identity-hub", "access-by-tenants"] as const,
  access: (accessId: string) =>
    ["identity-hub", "access", accessId] as const,
  stats: (window: IamWindow) =>
    ["identity-hub", "stats", window] as const,
  industries: () => ["identity-hub", "industries"] as const,
  industryBrands: (industry: string) => ["identity-hub", "industries", industry, "brands"] as const,
  industryBrandCounts: () => ["identity-hub", "industry-brand-counts"] as const,
  marketCatalogue: () => ["identity-hub", "market-catalogue"] as const,
  invites: () => ["identity-hub", "invites"] as const,
  invitesList: (filters: InviteListFilters) =>
    ["identity-hub", "invites", "list", filters] as const,
  /** Infinite-scroll cache — must not share a key with invitesList (different data shape). */
  invitesInfiniteList: (filters: InviteListFilters) =>
    ["identity-hub", "invites", "infinite", filters] as const,
  accessList: (filters: IamAccessListFilters) =>
    ["identity-hub", "access", "list", filters] as const,
  templates: (filters: AccessTemplateListFilters = {}) =>
    ["identity-hub", "templates", filters] as const,
  templatesPrefix: () => ["identity-hub", "templates"] as const,
  template: (templateId: string) =>
    ["identity-hub", "templates", templateId] as const,
  tenantSettings: (tenantId: string) =>
    ["identity-hub", "tenant-settings", tenantId] as const,
  tenantSyncRuns: (tenantId: string) =>
    ["identity-hub", "tenant-sync-runs", tenantId] as const,
  tenantPreflightMarketRefs: (tenantId: string) =>
    ["identity-hub", "tenant-preflight-market-refs", tenantId] as const,
} as const;

// ─── Query Options ──────────────────────────────────────────────────

export function adminTenantSettingsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: identityHubKeys.tenantSettings(tid || "__none__"),
    queryFn: () => fetchAdminTenantSettings(tid),
    enabled: !!tid,
    staleTime: 0,
  });
}

export function adminTenantSyncRunsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: identityHubKeys.tenantSyncRuns(tid || "__none__"),
    queryFn: () => fetchAdminTenantSyncRuns(tid),
    enabled: !!tid,
    staleTime: 0,
  });
}

export function adminTenantPreflightMarketRefsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: identityHubKeys.tenantPreflightMarketRefs(tid || "__none__"),
    queryFn: () => fetchAdminTenantPreflightMarketRefs(tid),
    enabled: !!tid,
    staleTime: 0,
  });
}

const TENANT_PICKER_PAGE_LIMIT = 100;

async function fetchTenantPickerTenants(): Promise<PaginatedTenants> {
  const firstPage = await fetchIamTenantsPage({
    limit: TENANT_PICKER_PAGE_LIMIT,
    sortBy: "name",
    sortOrder: "asc",
  });
  const items = [...firstPage.items];
  let page = firstPage;

  while (page.hasMore && page.nextCursor) {
    page = await fetchIamTenantsPage(
      {
        limit: TENANT_PICKER_PAGE_LIMIT,
        sortBy: "name",
        sortOrder: "asc",
      },
      page.nextCursor,
    );
    items.push(...page.items);
  }

  return {
    ...page,
    items,
    nextCursor: page.hasMore ? page.nextCursor : undefined,
    hasMore: page.hasMore && !!page.nextCursor,
    totalCount: firstPage.totalCount ?? page.totalCount,
  };
}

export function iamUsersInfiniteQueryOptions(filters: IamUsersListFilters) {
  return infiniteQueryOptions({
    queryKey: identityHubKeys.usersList(filters),
    queryFn: ({ pageParam }) => fetchIamUsersPage(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    meta: { skipGlobalError: true },
  });
}

/** @deprecated Use iamUsersInfiniteQueryOptions */
export function allUsersQueryOptions() {
  return queryOptions({
    queryKey: identityHubKeys.users(),
    queryFn: () => fetchIamUsersPage({ limit: 50 }),
  });
}

export function userQueryOptions(userId: string) {
  return queryOptions({
    queryKey: identityHubKeys.user(userId),
    queryFn: () => fetchUser(userId),
    enabled: !!userId,
  });
}

export function userAccessQueryOptions(userId: string) {
  return queryOptions({
    queryKey: identityHubKeys.userAccess(userId),
    queryFn: () => fetchUserAccess(userId),
    enabled: !!userId,
  });
}

/**
 * Batched access read for one stable chunk of user IDs. The dashboard fans out
 * one query per chunk so infinite scroll only fetches new chunks.
 */
export function usersAccessChunkQueryOptions(userIds: string[]) {
  return queryOptions({
    queryKey: identityHubKeys.usersAccess(userIds),
    queryFn: () => fetchUsersAccess(userIds),
    enabled: userIds.length > 0,
    staleTime: 60_000,
    // People-filter resets (opening a person from an org) change this key.
    // Keep the previous chunk visible so org counts do not flash 0.
    placeholderData: keepPreviousData,
  });
}

/**
 * Batched access read for the People tab — one request (or a handful, chunked)
 * for every loaded user instead of one per user. Prefer `usersAccessChunkQueryOptions`
 * via `useAllUsersAccess` so scroll growth does not refetch prior chunks.
 */
export function usersAccessQueryOptions(userIds: string[]) {
  return queryOptions({
    queryKey: identityHubKeys.usersAccess(userIds),
    queryFn: () => fetchUsersAccess(userIds),
    enabled: userIds.length > 0,
    placeholderData: keepPreviousData,
  });
}

export function iamTenantsInfiniteQueryOptions(filters: IamTenantsListFilters) {
  return infiniteQueryOptions({
    queryKey: identityHubKeys.tenantsList(filters),
    queryFn: ({ pageParam }) => fetchIamTenantsPage(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    meta: { skipGlobalError: true },
  });
}

export function allTenantsQueryOptions(opts: { includeArchived?: boolean } = {}) {
  return queryOptions({
    queryKey: identityHubKeys.tenants(opts),
    queryFn: () => fetchIamTenantsPage({ includeArchived: opts.includeArchived, limit: 50 }),
  });
}

export function tenantQueryOptions(tenantId: string) {
  return queryOptions({
    queryKey: identityHubKeys.tenant(tenantId),
    queryFn: () => fetchTenant(tenantId),
    enabled: !!tenantId,
  });
}

/** Tenant list for IAM form dropdowns. */
export function tenantPickerQueryOptions(opts: { enabled?: boolean } = {}) {
  return queryOptions({
    queryKey: identityHubKeys.tenantPicker(),
    queryFn: fetchTenantPickerTenants,
    staleTime: 60_000,
    enabled: opts.enabled ?? true,
  });
}

export function accessByTenantQueryOptions(tenantId: string, opts: { includeArchived?: boolean } = {}) {
  return queryOptions({
    queryKey: identityHubKeys.accessByTenant(tenantId, opts),
    queryFn: () => fetchAccessByTenant(tenantId, { includeArchived: opts.includeArchived }),
    enabled: !!tenantId,
  });
}

/**
 * Batched access-group read for one stable chunk of tenant IDs.
 */
export function accessGroupsByTenantsChunkQueryOptions(
  tenantIds: string[],
  opts: { includeArchived?: boolean } = {},
) {
  return queryOptions({
    queryKey: identityHubKeys.accessByTenants(tenantIds, opts),
    queryFn: () => fetchAccessGroupsByTenants(tenantIds, opts),
    enabled: tenantIds.length > 0,
    staleTime: 60_000,
  });
}

/**
 * Batched access-group read for the IAM dashboard — one request (chunked) for
 * every visible tenant instead of one per tenant. Prefer chunk queries via
 * `useAccessGroupsForTenants` so scroll growth does not refetch prior chunks.
 */
export function accessGroupsByTenantsQueryOptions(
  tenantIds: string[],
  opts: { includeArchived?: boolean } = {},
) {
  return queryOptions({
    queryKey: identityHubKeys.accessByTenants(tenantIds, opts),
    queryFn: () => fetchAccessGroupsByTenants(tenantIds, opts),
    enabled: tenantIds.length > 0,
    placeholderData: keepPreviousData,
  });
}

export function accessGroupQueryOptions(accessId: string) {
  return queryOptions({
    queryKey: identityHubKeys.access(accessId),
    queryFn: () => fetchAccessGroup(accessId),
    enabled: !!accessId,
  });
}

export function iamStatsQueryOptions(window: IamWindow) {
  return queryOptions({
    queryKey: identityHubKeys.stats(window),
    queryFn: () => fetchIamStats(window),
    staleTime: 60_000,
  });
}

export function allIndustriesQueryOptions() {
  return queryOptions({
    queryKey: identityHubKeys.industries(),
    queryFn: fetchAllIndustries,
    staleTime: 1000 * 60 * 60,
  });
}

export function industryBrandCountsQueryOptions(opts: { enabled?: boolean } = {}) {
  return queryOptions({
    queryKey: identityHubKeys.industryBrandCounts(),
    queryFn: ({ signal }) => fetchIndustryBrandCounts(signal),
    enabled: opts.enabled ?? true,
    staleTime: INDUSTRY_BRANDS_STALE_TIME_MS,
    retry: false,
  });
}

export function industryBrandOptionsQueryOptions(industry: string, opts: { enabled?: boolean } = {}) {
  return queryOptions({
    queryKey: identityHubKeys.industryBrands(industry),
    queryFn: ({ signal }) => fetchIndustryBrandOptions(industry, signal),
    enabled: (opts.enabled ?? true) && !!industry,
    staleTime: INDUSTRY_BRANDS_STALE_TIME_MS,
    retry: false,
  });
}

export function allMarketCatalogueQueryOptions() {
  return queryOptions({
    queryKey: identityHubKeys.marketCatalogue(),
    queryFn: fetchAllMarketCatalogue,
    staleTime: 1000 * 60 * 60,
    meta: { skipGlobalError: true },
  });
}

export function invitesInfiniteQueryOptions(filters: InviteListFilters) {
  return infiniteQueryOptions({
    queryKey: identityHubKeys.invitesInfiniteList(filters),
    queryFn: ({ pageParam }) => fetchAllInvites({ ...filters, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    meta: { skipGlobalError: true },
  });
}

export function invitesListQueryOptions(filters: InviteListFilters) {
  return queryOptions({
    queryKey: identityHubKeys.invitesList(filters),
    queryFn: () => fetchAllInvites(filters),
    staleTime: 30_000,
  });
}

export function iamAccessInfiniteQueryOptions(filters: IamAccessListFilters) {
  return infiniteQueryOptions({
    queryKey: identityHubKeys.accessList(filters),
    queryFn: ({ pageParam }) => fetchIamAccessPage(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function accessTemplatesQueryOptions(filters: AccessTemplateListFilters = {}) {
  return queryOptions({
    queryKey: identityHubKeys.templates(filters),
    queryFn: () => fetchAccessTemplates(filters),
    staleTime: 30_000,
    meta: { skipGlobalError: true },
  });
}

export function accessTemplateQueryOptions(templateId: string) {
  return queryOptions({
    queryKey: identityHubKeys.template(templateId),
    queryFn: () => fetchAccessTemplate(templateId),
    enabled: !!templateId,
    staleTime: 30_000,
  });
}
